import { useState, useMemo } from "react";
import { Search, Filter, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ToastHelper from "@/utils/toastHelper";
import { gridExportService } from "@/services/gridExportService";

export default function DataListPage({ title, subtitle, columns, rows, testidPrefix, statusStyles = {}, actions, extraFilter, addButton, onAdd, rowClassName }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(() => {
    let list = rows;
    if (q) list = list.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));
    if (filter !== "all" && extraFilter) list = list.filter((r) => extraFilter(r, filter));
    return list;
  }, [rows, q, filter, extraFilter]);

  const exportPayload = () => ({
    title,
    columns: columns.map((column) => column.label),
    rows: filtered.map((row) => columns.map((column) => {
      const value = row[column.key];
      if (value === null || value === undefined) return "";
      return column.type === "money" ? `$${value}` : String(value);
    })),
  });
  const exportGrid = async (format) => {
    try {
      await (format === "excel" ? gridExportService.exportExcel(exportPayload()) : gridExportService.exportPdf(exportPayload()));
      ToastHelper.success(`Downloaded ${filtered.length} rows as ${format === "excel" ? "Excel" : "PDF"}`);
    } catch (error) {
      ToastHelper.apiError(error, `Unable to export ${format === "excel" ? "Excel" : "PDF"}`);
    }
  };

  return (
    <div className="space-y-5" data-testid={`${testidPrefix}-page`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">{title}</h1>
          {subtitle && <div className="text-sm text-slate-500">{subtitle}</div>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportGrid("excel")} className="wc-btn-outline text-xs" data-testid={`${testidPrefix}-export-csv`}>Export Excel</button>
          <button onClick={() => exportGrid("pdf")} className="wc-btn-outline text-xs" data-testid={`${testidPrefix}-export-pdf`}>Export PDF</button>
          {addButton && <button onClick={onAdd || (() => ToastHelper.success("Create dialog opened"))} className="wc-btn-primary text-xs" data-testid={`${testidPrefix}-add`}>{addButton}</button>}
        </div>
      </div>

      <div className="wc-card p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${title.toLowerCase()}...`} className="wc-input pl-9" data-testid={`${testidPrefix}-search`} />
          </div>
          {extraFilter && (
            <div className="flex items-center gap-1">
              <Filter className="w-4 h-4 text-slate-400" />
              {["all", "active", "pending", "completed"].map((k) => (
                <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === k ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-slate-700 border-slate-200"}`}>{k[0].toUpperCase() + k.slice(1)}</button>
              ))}
            </div>
          )}
          <div className="text-sm text-slate-500">{filtered.length} of {rows.length}</div>
        </div>
      </div>

      <div className="wc-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase font-semibold text-slate-500">
              <tr>{columns.map((c) => <th key={c.key} className={`px-4 py-3 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>)}<th className="w-12"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((row, idx) => (
                <tr key={row.id || idx} className={`hover:bg-slate-50 ${rowClassName ? rowClassName(row) : ""}`}>
                  {columns.map((c) => (
                    <td key={c.key} className={`px-4 py-3 ${c.align === "right" ? "text-right" : ""}`}>
                      {c.render ? c.render(row) :
                        c.type === "status" ? <span className={`wc-badge ${statusStyles[row[c.key]] || ""}`}>{row[c.key]}</span> :
                        c.type === "money" ? `$${row[c.key]}` :
                        row[c.key]}
                    </td>
                  ))}
                  <td className="px-2 py-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger className="w-8 h-8 rounded hover:bg-slate-100 inline-flex items-center justify-center" data-testid={`${testidPrefix}-row-menu`}><MoreHorizontal className="w-4 h-4" /></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {(actions || [
                          { label: "View", fn: () => ToastHelper.info(`Viewing ${row.id}`) },
                          { label: "Edit", fn: () => ToastHelper.info(`Editing ${row.id}`) },
                          { label: "Delete", fn: () => ToastHelper.error(`Deleted ${row.id}`), destructive: true },
                        ]).filter((a) => !(typeof a.hidden === "function" ? a.hidden(row) : a.hidden)).map((a) => (
                          <DropdownMenuItem key={a.label} onClick={() => a.fn(row)} className={a.destructive ? "text-rose-600" : ""}>{a.label}</DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-16 text-center text-slate-400">No results found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
